<template>
  <Dialog
    :visible.sync="showConfigModal"
    :style="{ width: '950px', minHeight: '600px' }"
    :modal="true"
    :closable="false"
    class="insights-config-modal"
  >
  
  <template #header>
      <div class="modal-header">
        <h2>{{ editMode ? 'Editar Insight' : 'Configurar Insight' }}</h2>
        <Button
          icon="pi pi-times"
          class="p-button-rounded p-button-text"
          @click="closeModal"
        />
      </div>
    </template>

    <div class="modal-content">
      <TabView>
        <TabPanel header="Configurações">
          <!-- Título do Insight -->
          <div class="form-group">
            <label for="insightTitle">Título do Insight *</label>
            <InputText
              id="insightTitle"
              v-model="config.title"
              class="w-full"
              :class="{ 'p-invalid': showValidation && !config.title }"
              placeholder="Digite o título do insight"
            />
            <small v-if="showValidation && !config.title" class="p-error">Título é obrigatório</small>
          </div>

          <!-- Estado e Cidade (apenas para social-media) -->
          <div class="form-group" v-if="module === 'social_media'">
            <label>Estado e Cidade *</label>
            <StateCitySelector
              v-if="statesData && statesData.length"
              :states="statesData"
              :defaultState="String(config.moduleFields[module].state || '')"
              :defaultCity="String(config.moduleFields[module].city || '')"
              @update:city="handleCityUpdate"
              @update:state="handleStateUpdate"
            />
            <small v-if="showValidation && (!config.moduleFields[module].state || !config.moduleFields[module].city)" class="p-error">Estado e cidade são obrigatórios</small>
          </div>

          <!-- Termo de Pesquisa (apenas para social-media) -->
          <div class="form-group" v-if="module === 'social_media'">
            <label for="searchTerm">Termo de Pesquisa *</label>
            <Dropdown
              id="searchTerm"
              v-model="config.moduleFields[module].search_term"
              :options="searchTerms"
              optionLabel="value"
              optionValue="id"
              placeholder="Selecione um termo de pesquisa"
              class="w-full"
              :class="{ 'p-invalid': showValidation && !config.moduleFields[module].search_term }"
              @change="handleSearchTermUpdate($event.value)"
            />
            <small v-if="showValidation && !config.moduleFields[module].search_term" class="p-error">Termo de pesquisa é obrigatório</small>
          </div>

          <!-- Reasoning -->
          <div class="form-group">
            <div class="reasoning-toggle">
              <Checkbox
                v-model="config.useReasoning"
                :binary="true"
                inputId="reasoning"
              />
              <label for="reasoning">
                <i class="pi pi-sitemap"></i>
                Usar Reason
              </label>
            </div>
          </div>

          <!-- Número de Rodadas -->
          <div class="form-group">
            <label for="rounds">
              <i class="pi pi-sync"></i>
              Número de Rodadas *
            </label>
            <div class="rounds-input-container">
              <div class="rounds-slider">
                <Slider
                  v-model="config.rounds"
                  :min="1"
                  :max="10"
                  :step="1"
                  class="w-full"
                />
              </div>
              <div class="rounds-number">
                <InputNumber
                  v-model="config.rounds"
                  :min="1"
                  :max="10"
                  :step="1"
                  class="w-full"
                  :disabled="true"
                  :readonly="true"
                  :showButtons="false"
                />
              </div>
            </div>
          </div>

          <!-- Papel do Usuário -->
          <div class="form-group">
            <label for="roleId">
              <i class="pi pi-user-edit"></i>
              Papel do Usuário *
            </label>
            <select
              v-model="config.role_id"
              id="roleId"
              class="form-control form-select w-full"
              :class="{ 'p-invalid': showValidation && !config.role_id }"
              @change="$emit('get-role-id', $event.target.value)"
            >
              <option :value="null" disabled>Selecione o papel do usuário</option>
              <option v-for="role in roles" :key="role.id" :value="Number(role.id)">
                {{ role.name }}
              </option>
            </select>
            <small v-if="showValidation && !config.role_id" class="p-error">Papel do usuário é obrigatório</small>
          </div>

          <!-- Prompt do Usuário -->
          <div class="form-group">
            <label for="userPrompt">
              <i class="pi pi-user"></i>
              Prompt do Usuário *
            </label>
            <Textarea
              id="userPrompt"
              v-model="config.text"
              rows="5"
              class="w-full"
              :class="{ 'p-invalid': showValidation && !config.text }"
              placeholder="Digite seu prompt..."
            />
            <small v-if="showValidation && !config.text" class="p-error">Prompt do usuário é obrigatório</small>
          </div>
          
          <!-- Fontes de Dados (apenas para o módulo de reviews) -->
          <div class="form-group" v-if="module === 'reviews' && dataSourceOptions && dataSourceOptions.length > 0">
            <label for="dataSources">
              <i class="pi pi-database"></i>
              Fontes de Dados *
            </label>
            <MultiSelect
              id="dataSources"
              v-model="config.selectedDataSources"
              :options="dataSourceOptions"
              optionLabel="label"
              optionValue="id"
              :filter="true"
              placeholder="Selecione as fontes de dados"
              class="w-full"
              :class="{ 'p-invalid': showValidation && (!config.selectedDataSources || config.selectedDataSources.length === 0) }"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value && slotProps.value.length > 0" class="multiselect-value">
                  {{ slotProps.value.length }} fonte{{ slotProps.value.length > 1 ? 's' : '' }} selecionada{{ slotProps.value.length > 1 ? 's' : '' }}
                </div>
                <div v-else class="multiselect-placeholder">
                  Selecione as fontes de dados
                </div>
              </template>
            </MultiSelect>
            <small v-if="showValidation && (!config.selectedDataSources || config.selectedDataSources.length === 0)" class="p-error">Selecione pelo menos uma fonte de dados</small>
          </div>

          <!-- Fontes de Dados (apenas para o módulo de social_media) -->
          <div class="form-group" v-if="module === 'social_media' && dataSourceOptions && dataSourceOptions.length > 0">
            <label for="socialMediaDataSources">
              <i class="pi pi-database"></i>
              Fontes de Dados *
            </label>
            <MultiSelect
              id="socialMediaDataSources"
              v-model="config.selectedDataSources"
              :options="dataSourceOptions"
              optionLabel="label"
              optionValue="id"
              :filter="true"
              placeholder="Selecione as fontes de dados"
              class="w-full"
              :class="{ 'p-invalid': showValidation && (!config.selectedDataSources || config.selectedDataSources.length === 0) }"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value && slotProps.value.length > 0" class="multiselect-value">
                  {{ slotProps.value.length }} fonte{{ slotProps.value.length > 1 ? 's' : '' }} selecionada{{ slotProps.value.length > 1 ? 's' : '' }}
                </div>
                <div v-else class="multiselect-placeholder">
                  Selecione as fontes de dados
                </div>
              </template>
            </MultiSelect>
            <small v-if="showValidation && (!config.selectedDataSources || config.selectedDataSources.length === 0)" class="p-error">Selecione pelo menos uma fonte de dados</small>
          </div>

        </TabPanel>

        <TabPanel>
          <template #header>
            <div class="flex align-items-center">
              <span>Personas</span>
            </div>
          </template>
          <div class="form-group">
            <label>Personas *</label>
            <div class="personas-container">
              <div class="personas-grid">
                <div
                  v-for="persona in personas"
                  :key="persona.id"
                  class="persona-card"
                  :class="{ 
                    'selected': isPersonaSelected(persona.id),
                    'disabled': isDefaultPersona(persona.id)
                  }"
                  @click="isDefaultPersona(persona.id) ? null : togglePersona(persona.id)"
                >
                  <div class="persona-header">
                    <div class="persona-avatar">
                      <img 
                        v-if="persona.avatar" 
                        :src="persona.avatar" 
                        :alt="persona.name" 
                        class="avatar-image"
                      />
                      <div v-else class="avatar-letter">
                        {{ getFirstLetter(persona.name) }}
                      </div>
                    </div>
                    <div class="persona-info">
                      <h3>{{ persona.name }}</h3>
                      <span class="persona-role">{{ persona.role }}</span>
                    </div>
                  </div>
                  
                  <!-- Sistema truncado -->
                  <div class="system-prompt-preview">
                    {{ truncateText(persona.system_prompt, 200) }}
                    <span v-if="persona.system_prompt && persona.system_prompt.length > 30"></span>
                  </div>
                  
                  <!-- Botão de seleção -->
                  <button 
                    class="select-button"
                    :class="{ 
                      'selected': isPersonaSelected(persona.id),
                      'disabled': isDefaultPersona(persona.id)
                    }"
                    @click.stop="isDefaultPersona(persona.id) ? null : togglePersona(persona.id)"
                    :disabled="isDefaultPersona(persona.id)"
                  >
                    <i :class="['pi', isPersonaSelected(persona.id) ? 'pi-check' : 'pi-plus']"></i>
                    <span>{{ isPersonaSelected(persona.id) ? 'Selecionado' : 'Selecionar' }}</span>
                  </button>
                </div>
              </div>
            </div>
            <small v-if="showValidation && config.selectedPersonas.length === 0" class="p-error">Selecione pelo menos uma persona</small>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <template #footer>
      <div class="modal-footer">
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeModal"
        />
        <Button
          label="Salvar"
          icon="pi pi-check"
          class="p-button-primary"
          @click="saveConfig"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import Slider from 'primevue/slider'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import StateCitySelector from '@/Pages/Reviews/Components/StateCitySelector/StateCitySelector.vue'
import axios from 'axios'
import { useInsightStore } from '@/stores/insightStore'
import { useStateCityStore } from '@/Stores/stateCityStore'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'

export default {
  name: 'InsightsConfigModal',
  components: {
    Dialog,
    Button,
    InputText,
    InputNumber,
    Textarea,
    Checkbox,
    Slider,
    TabView,
    TabPanel,
    StateCitySelector,
    MultiSelect,
    Dropdown
  },
  props: {
    showConfigModal: {
      type: Boolean,
      default: false
    },
    module: {
      type: String,
      required: true
    },
    moduleId: {
      type: [Number, String, null],
      default: null
    },
    states: {
      type: Array,
      default: () => []
    },
    roles: {
      type: Array,
      default: () => []
    },
    editMode: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    },
    dataSourceOptions: {
      type: Array,
      default: () => []
    },
    socialMediaDataSourceOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const defaultPersonaIds = ['consensus_builder', 'consensus_reviewer', 'consensus_init'];
    // Initialize moduleFields with base structure
    const moduleFields = {
      reviews: {
        state: '',
        state_name: '',
        city: ''
      },
      social_media: {
        state: '',
        state_name: '',
        city: '',
        search_term: ''
      }
    };
    
    // Inicializar com valores padrão
    return {
      defaultPersonaIds,
      personas: [],
      loadingPersonas: false,
      showValidation: false,
      statesData: [], // Renamed from states to avoid conflict with prop
      searchTerms: [], // Array para armazenar os termos de pesquisa
      config: {
        title: '',
        text: '',
        rounds: 1,
        useReasoning: true,
        role_id: null, // Inicializado como null
        selectedPersonas: [...defaultPersonaIds],
        selectedDataSources: [],
        moduleFields
      },
      editDataCache: null // Cache para os dados de edição
    };
  },
  mounted() {
    console.log('InsightsConfigModal mounted, module:', this.module);
    this.fetchPersonas();
    if (this.editMode && this.editData) {
      this.loadEditData();
    }
    // Buscar estados
    this.fetchStates();
    // Buscar termos de pesquisa
    this.fetchSearchTerms();
  },
  watch: {
    module: {
      immediate: true,
      handler(newVal) {
        // Garantir que a estrutura para o novo módulo existe
        if (newVal && !this.config.moduleFields[newVal]) {
          this.$set(this.config.moduleFields, newVal, {
            state: '',
            state_name: '',
            city: ''
          });
        }
      }
    },
    showConfigModal: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchPersonas()
        }
      }
    }
  },
  methods: {
    handleStateUpdate(state) {
      if (this.module === 'social_media' && this.config.moduleFields[this.module]) {
        // Garantir que o estado seja sempre uma string
        this.config.moduleFields[this.module].state = String(state || '');
        
        // Obter o nome do estado
        const stateObj = this.statesData.find(s => String(s.id) === String(state));
        if (stateObj) {
          this.config.moduleFields[this.module].state_name = stateObj.name;
        }
      }
    },
    
    handleCityUpdate(city) {
      if (this.module === 'social_media' && this.config.moduleFields[this.module]) {
        // Garantir que a cidade seja sempre uma string
        this.config.moduleFields[this.module].city = String(city || '');
      }
    },
    
    async fetchPersonas() {
      try {
        const response = await axios.get('/api/set-ia-consensus/personas')
        this.personas = response.data
      } catch (error) {
        console.error('Erro ao buscar personas:', error)
      }
    },
    async fetchSearchTerms() {
      try {
        // Usar a mesma URL que a função handleSocialValues
        const user = this.$page.props.auth.user.id;
        const response = await axios.get(`/api/social-media/${user}`);
        this.searchTerms = response.data;
      } catch (error) {
        console.error('Erro ao buscar termos de pesquisa:', error)
      }
    },
    async fetchStates() {
      try {
        const response = await axios.get('/api/states');
        this.statesData = response.data;
      } catch (error) {
        console.error('Erro ao buscar estados:', error)
      }
    },
    isPersonaSelected(personaId) {
      return this.config.selectedPersonas.includes(personaId)
    },

    isDefaultPersona(personaId) {
      return this.defaultPersonaIds.includes(personaId)
    },

    togglePersona(personaId) {
      // Se for uma persona padrão, não permite alteração
      if (this.isDefaultPersona(personaId)) {
        return
      }

      const index = this.config.selectedPersonas.indexOf(personaId)
      if (index === -1) {
        this.config.selectedPersonas.push(personaId)
      } else {
        this.config.selectedPersonas.splice(index, 1)
      }
    },

    closeModal() {
      this.showValidation = false
      this.$emit('update:showConfigModal', false)
    },
    validateForm() {
      let isValid = true;
      
      // Validar campos obrigatórios
      if (!this.config.title) {
        isValid = false;
      }
      
      if (!this.config.text) {
        isValid = false;
      }
      
      // Validar campos específicos por módulo
      if (this.module === 'social_media') {
        if (!this.config.moduleFields[this.module].state || !this.config.moduleFields[this.module].city) {
          isValid = false;
        }
        
        if (!this.config.moduleFields[this.module].search_term) {
          isValid = false;
        }
        
        // Validar fontes de dados para social_media
        if (!this.config.selectedDataSources || this.config.selectedDataSources.length === 0) {
          isValid = false;
        }
      } else if (this.module === 'reviews') {
        // Validar fontes de dados para reviews
        if (!this.config.selectedDataSources || this.config.selectedDataSources.length === 0) {
          isValid = false;
        }
      }
      
      this.showValidation = !isValid;
      return isValid;
    },
    saveConfig() {
      if (!this.validateForm()) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro de Validação',
          detail: 'Por favor, preencha todos os campos obrigatórios.',
          life: 5000
        });
        return;
      }

      // Filtrar personas inválidas antes de salvar
      const validPersonaIds = this.config.selectedPersonas.filter(id => {
        // Filtrar a persona 'coordinator_initial_context' que não existe no banco
        return id !== 'coordinator_initial_context';
      });

      // Garantir que os campos de estado e cidade sejam strings
      if (this.module === 'social_media' && this.config.moduleFields[this.module]) {
        this.config.moduleFields[this.module].state = String(this.config.moduleFields[this.module].state || '');
        this.config.moduleFields[this.module].city = String(this.config.moduleFields[this.module].city || '');
        this.config.moduleFields[this.module].search_term = String(this.config.moduleFields[this.module].search_term || '');
      }

      // Preparar os dados para salvar
      const insightData = {
        title: this.config.title,
        text: this.config.text,
        module: this.module,
        role_id: this.config.role_id,
        rounds: this.config.rounds,
        use_reasoning: this.config.useReasoning,
        moduleFields: this.config.moduleFields,
        selectedPersonas: validPersonaIds,
        selectedDataSources: this.config.selectedDataSources,
        // Adicionar o ID do insight se estiver em modo de edição
        id: this.editMode && this.editDataCache ? this.editDataCache.id : undefined,
        // Adicionar o ID do módulo se estiver disponível
        module_id: this.moduleId !== undefined ? this.moduleId : null,
        // Mapear personas para o formato esperado pelo backend
        agents: validPersonaIds.map(personaId => {
          const persona = this.personas.find(p => String(p.id) === String(personaId));
          if (!persona) return null;
          
          return {
            id: persona.id,
            agent_name: persona.name,
            agent_role: persona.role,
            agent_expertise: persona.expertise,
            agent_description: persona.description,
            agent_system_prompt: persona.system_prompt
          };
        }).filter(agent => agent !== null) // Remover quaisquer agentes nulos
      };
      
      // Usar a store Pinia
      const insightStore = useInsightStore();
      
      // Log para depuração
      console.log('Dados a serem enviados:', JSON.stringify(insightData, null, 2));
      
      // Salvar os dados
      insightStore.setInsightData(insightData);

      // Emitir evento para salvar
      this.$emit('save', insightData);
      this.closeModal();
    },
    loadEditData(editData) {
      if (!editData) return;
      
      this.editDataCache = JSON.parse(JSON.stringify(editData)); // Criar uma cópia profunda para cache
      
      // Resetar a configuração
      this.config = {
        title: '',
        text: '',
        rounds: 1,
        useReasoning: true,
        role_id: null,
        selectedPersonas: [...this.defaultPersonaIds],
        selectedDataSources: [],
        moduleFields: {
          reviews: {
            state: '',
            state_name: '',
            city: ''
          },
          social_media: {
            state: '',
            state_name: '',
            city: '',
            search_term: ''
          }
        }
      };
      
      // Preencher dados básicos
      this.config.title = editData.title || '';
      this.config.text = editData.text || '';
      this.config.role_id = editData.role_id || null;
      this.config.rounds = editData.rounds || 1;
      this.config.useReasoning = editData.use_reasoning !== undefined ? editData.use_reasoning : true;
      
      // Preencher campos específicos por módulo
      const module = editData.module;
      
      if (module === 'social_media') {
        // Usar this.$set para garantir reatividade
        this.$set(this.config.moduleFields.social_media, 'state', editData.state || '');
        this.$set(this.config.moduleFields.social_media, 'state_name', editData.state_name || '');
        this.$set(this.config.moduleFields.social_media, 'city', editData.city || '');
        this.$set(this.config.moduleFields.social_media, 'search_term', editData.search_term || '');
      } else if (module === 'reviews') {
        // Sem campos específicos para carregar no módulo reviews por enquanto
      }
      
      // Preencher fontes de dados
      if (editData.data_sources) {
        try {
          const dataSources = typeof editData.data_sources === 'string' 
            ? JSON.parse(editData.data_sources) 
            : editData.data_sources;
            
          this.config.selectedDataSources = Array.isArray(dataSources) ? dataSources : [];
        } catch (e) {
          console.error('Erro ao processar fontes de dados:', e);
          this.config.selectedDataSources = [];
        }
      }
      
      // Preencher personas selecionadas
      if (editData.debate_data && editData.debate_data.personas) {
        const personaIds = editData.debate_data.personas
          .filter(p => p.id) // Filtrar apenas personas com ID
          .map(p => p.id);   // Extrair apenas os IDs
          
        this.config.selectedPersonas = [
          ...this.defaultPersonaIds, // Garantir que as personas padrão estão incluídas
          ...personaIds.filter(id => !this.defaultPersonaIds.includes(id)) // Adicionar outras personas
        ];
      }
    },
    getFirstLetter(name) {
      return name.charAt(0).toUpperCase();
    },
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    handleSearchTermUpdate(newTerm) {
      if (newTerm !== null && newTerm !== undefined) {
        this.config.moduleFields[this.module].search_term = newTerm;
        
        // Atualizar o store
        const store = useStateCityStore();
        store.setSearchTerm(newTerm);
      }
    },
  }
}
</script>

<style scoped>
.insights-config-modal {
  font-family: 'Inter', sans-serif;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
}

.modal-content {
  padding: 1.5rem;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-group label i {
  color: #3b82f6;
  font-size: 1.1rem;
}

.personas-container {
  padding: 1rem;
  max-height: 400px; /* Limitar altura para evitar scroll vertical excessivo */
}

.personas-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding-bottom: 0.5rem;
}

.persona-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 170px; /* Aumentado ligeiramente para acomodar o avatar */
}

.persona-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.persona-card.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.persona-header {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.persona-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-letter {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #3b82f6, #60a5fa);
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  text-transform: uppercase;
}

.persona-info {
  flex: 1;
  min-width: 0; /* Permite que o texto seja truncado */
}

.persona-info h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.persona-role {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.system-prompt-preview {
  font-size: 0.75rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
  flex-grow: 1;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.select-button {
  width: 100%;
  height: 32px;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  font-weight: 500;
  margin-top: auto;
  font-size: 0.75rem;
}

.persona-card.selected {
  border-color: #3b82f6;
  background-color: #f0f7ff;
}

.persona-card.disabled .select-button {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  color: #9ca3af;
}

.persona-card.disabled .select-button:hover {
  transform: none;
  border-color: #d1d5db;
  color: #9ca3af;
}

.persona-card.disabled .select-button:focus {
  box-shadow: none;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

:deep(.p-dialog .p-dialog-content) {
  padding: 0;
  background-color: #f9fafb;
  overflow-y: auto; /* Adicionar scroll vertical quando necessário */
  display: flex;
  flex-direction: column;
  min-height: 600px !important;
  max-height: 80vh;
}

:deep(.p-dialog-content) {
  flex: 1;
  min-height: 600px !important;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Garantir que TabView ocupe todo o espaço disponível */
:deep(.p-tabview) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.p-tabview-panels) {
  flex: 1;
  min-height: 500px;
  overflow-y: auto;
}

:deep(.p-inputtext),
:deep(.p-inputnumber),
:deep(.p-textarea) {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

:deep(.p-inputtext:focus),
:deep(.p-inputnumber:focus),
:deep(.p-textarea:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

:deep(.p-checkbox) {
  margin-right: 0.5rem;
}

:deep(.p-checkbox .p-checkbox-box) {
  border-radius: 0.25rem;
}

:deep(.p-checkbox .p-checkbox-box.p-highlight) {
  background: #3b82f6;
  border-color: #3b82f6;
}

.rounds-input-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.rounds-input-container:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.rounds-slider {
  padding: 0.5rem 0;
}

.rounds-number {
  display: flex;
  justify-content: center;
}

:deep(.p-slider) {
  height: 0.5rem;
}

:deep(.p-slider .p-slider-handle) {
  width: 1.25rem;
  height: 1.25rem;
  background: #3b82f6;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.p-slider .p-slider-range) {
  background: #3b82f6;
}

:deep(.p-inputnumber) {
  width: 120px;
}

:deep(.p-inputnumber .p-inputtext) {
  text-align: center;
  font-weight: 600;
  color: #1f2937;
}

:deep(.p-inputnumber-buttons-stacked .p-button) {
  background: #f3f4f6;
  border: none;
  color: #6b7280;
  transition: all 0.2s ease;
}

:deep(.p-inputnumber-buttons-stacked .p-button:hover) {
  background: #e5e7eb;
  color: #3b82f6;
}

:deep(.p-inputnumber-buttons-stacked .p-button:focus) {
  box-shadow: none;
}

.insights-config-modal :deep(.p-dialog) {
  min-height: 650px;
  display: flex;
  flex-direction: column;
}

.insights-config-modal :deep(.p-dialog-content) {
  flex: 1;
  min-height: 600px !important;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Garantir que TabView ocupe todo o espaço disponível */
.insights-config-modal :deep(.p-tabview) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.insights-config-modal :deep(.p-tabview-panels) {
  flex: 1;
  min-height: 500px;
  overflow-y: auto;
}

/* Estilos para o MultiSelect */
.multiselect-value {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #4b5563;
}

.multiselect-placeholder {
  color: #9ca3af;
  font-size: 0.875rem;
}

:deep(.p-multiselect) {
  width: 100%;
  border-radius: 0.5rem;
}

:deep(.p-multiselect:not(.p-disabled).p-focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

:deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item.p-highlight) {
  background-color: #f0f7ff;
  color: #3b82f6;
}

:deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item:hover) {
  background-color: #f0f7ff;
}
</style>